import { doc, getDoc, writeBatch } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { InsuranceCustomerAccount } from '@team-milva/lab-models/insuranceCustomerAccount'
import type { IInsuranceCustomerAccountPrice } from '@team-milva/lab-models/insuranceCustomerAccount'
import { LabCustomerUserInsurance } from '@team-milva/lab-models/user'
import type { IDropPointPrivate } from '@team-milva/lab-models/resources'
import { insuranceCustomerRef } from '@team-milva/lab-models/customer'
import type { ICustomerRef } from '@team-milva/lab-models/customer'

/**
 * Seed fixtures for an insurance customer's own users, drop points and orders (MIL-402).
 *
 * An insurance customer gets one user and one private drop point. The insurance customer itself is
 * the first active one alphabetically, and only created when the lab has none at all.
 *
 * **The seeded user is data only.** There is no Auth emulator in this workspace (see
 * `.claude/instructions/local-dev-auth.md`), and a client-side seed cannot create a real Firebase
 * Auth account, so the user appears in lab-management (Brugere tab, order owner, notifications)
 * but cannot sign in to the customer app. Create a real one through Forsikringskunder → Brugere for
 * that.
 *
 * Every ID is deterministic, so re-running overwrites the same documents instead of piling up.
 */

/** The seeded insurance user's address — `ai-test+…`, so mail lands in the readable test inbox. */
export const SEED_INSURANCE_USER_EMAIL = 'ai-test+seed-insurance-user@milva.io'

export interface ISeedInsuranceCustomerFixtures {
  insuranceCustomerAccount: InsuranceCustomerAccount
  /** The owner reference every insurance-owned document carries. */
  customer: ICustomerRef & { type: 'insurance' }
  user: LabCustomerUserInsurance
  dropPoint: IDropPointPrivate
  /** Whether the insurance customer itself had to be created. */
  createdInsuranceCustomerAccount: boolean
}

export function seedInsuranceUserID(insuranceCustomerAccountID: string): string {
  return `dev-seed-insurance-user-${insuranceCustomerAccountID}`
}

export function seedInsuranceDropPointID(insuranceCustomerAccountID: string): string {
  return `dev-seed-insurance-drop-point-${insuranceCustomerAccountID}`
}

/** The insurance customer created when the lab has none. Fixed ID, so a re-run reuses it. */
export function seedInsuranceCustomerAccountID(labAccountID: string): string {
  return `dev-seed-insurance-customer-${labAccountID}`
}

/** The user document, as `createCustomerUserRecord` in lab-cloud writes it — minus the Auth account. */
export function buildSeedInsuranceUser({
  labAccountID,
  insuranceCustomerAccountID,
  createdByUserID,
  now,
}: {
  labAccountID: string
  insuranceCustomerAccountID: string
  createdByUserID: string
  now: number
}): LabCustomerUserInsurance {
  return new LabCustomerUserInsurance({
    id: seedInsuranceUserID(insuranceCustomerAccountID),
    labAccountID,
    customer: insuranceCustomerRef(insuranceCustomerAccountID),
    insuranceCustomerAccountID,
    createdByUserID,
    invitedByUserID: '',
    firstName: 'Forsikring',
    lastName: 'Seedbruger',
    email: SEED_INSURANCE_USER_EMAIL,
    phone: { number: 0, iso2: 'dk' },
    mainRole: 'admin',
    activeState: 'active',
    timestamps: { createdAt: now, updatedAt: now, lastSeenAt: 0, deactivatedAt: 0 },
  })
}

/** The private drop point, as `createPrivateDropPointService` in lab-cloud writes it. */
export function buildSeedInsuranceDropPoint({
  labAccountID,
  insuranceCustomerAccountID,
  createdByUserID,
  now,
}: {
  labAccountID: string
  insuranceCustomerAccountID: string
  createdByUserID: string
  now: number
}): IDropPointPrivate {
  return {
    id: seedInsuranceDropPointID(insuranceCustomerAccountID),
    labAccountID,
    createdByUserID,
    type: 'private',
    customer: insuranceCustomerRef(insuranceCustomerAccountID),
    // Deprecated (MIL-505): the business customer's ID only — `''` for an insurance customer.
    customerID: '',
    placement: { da: 'I receptionen' },
    address: { address: 'Forsikringsvej 1', postalCode: '2100', city: 'København Ø' },
    activeState: 'active',
    timestamps: { createdAt: now, updatedAt: now, deactivatedAt: 0 },
  }
}

/**
 * Ensures the fixtures exist and returns them. `existing` is the first active insurance customer
 * the caller already holds (from the store); when null, one is created — linked to
 * `linkToCustomerAccountID` so the insurance-case blueprints of `runSeedOrders` find it, and with a
 * price document on `pricingSegment` so its own orders are priced.
 */
export async function ensureInsuranceCustomerFixtures({
  labAccountID,
  userID,
  existing,
  linkToCustomerAccountID,
  pricingSegment,
  defaultLocale,
}: {
  labAccountID: string
  userID: string
  existing: InsuranceCustomerAccount | null
  linkToCustomerAccountID: string | null
  pricingSegment: { id: string; currencyCode: IInsuranceCustomerAccountPrice['currencyCode'] } | null
  defaultLocale: InsuranceCustomerAccount['defaultLocale']
}): Promise<ISeedInsuranceCustomerFixtures> {
  const now = Date.now()
  const batch = writeBatch(db)

  let insuranceCustomerAccount = existing
  const createdInsuranceCustomerAccount = !insuranceCustomerAccount
  if (!insuranceCustomerAccount) {
    const id = seedInsuranceCustomerAccountID(labAccountID)
    insuranceCustomerAccount = new InsuranceCustomerAccount({
      id,
      labAccountID,
      insuranceCustomerAccountID: id,
      name: 'DEV Forsikring A/S',
      activeState: 'active',
      timestamps: { createdAt: now, updatedAt: now, deactivatedAt: 0 },
      createdByUserID: userID,
      supportedLocales: [defaultLocale],
      defaultLocale,
      customerAccountIDs: linkToCustomerAccountID ? [linkToCustomerAccountID] : [],
    })
    batch.set(doc(db, insuranceCustomerAccount.referencePaths.self), { ...insuranceCustomerAccount })

    const pricingRef = doc(db, insuranceCustomerAccount.referencePaths.pricing)
    if (pricingSegment && !(await getDoc(pricingRef)).exists()) {
      const pricing: IInsuranceCustomerAccountPrice = {
        id,
        labAccountID,
        insuranceCustomerAccountID: id,
        pricingSegmentID: pricingSegment.id,
        currencyCode: pricingSegment.currencyCode,
        analyses: [],
        tats: [],
        addons: [],
        deals: [],
        createdByUserID: userID,
        timestamps: { createdAt: now, updatedAt: now },
      }
      batch.set(pricingRef, pricing)
    }
  }

  const user = buildSeedInsuranceUser({
    labAccountID,
    insuranceCustomerAccountID: insuranceCustomerAccount.id,
    createdByUserID: userID,
    now,
  })
  batch.set(doc(db, 'Apps', '-lab-customer-', 'Users', user.id), { ...user })

  const dropPoint = buildSeedInsuranceDropPoint({
    labAccountID,
    insuranceCustomerAccountID: insuranceCustomerAccount.id,
    createdByUserID: userID,
    now,
  })
  batch.set(doc(db, 'Apps', '-lab-customer-', 'PrivateDropPoints', dropPoint.id), dropPoint)

  await batch.commit()

  return {
    insuranceCustomerAccount,
    customer: { type: 'insurance', id: insuranceCustomerAccount.id },
    user,
    dropPoint,
    createdInsuranceCustomerAccount,
  }
}
